import { useMemo } from "react"
import { Box, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { HIDDEN_IN_PRINT } from "../../../../../../shared/components/print/printPageOnly"
import { usePrintPageOnly } from "../../../../../../shared/hooks/usePrintPageOnly"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useCurrentServiceCenter } from "../../../../store/serviceCenterHooks"
import { fetchGuests, ServiceCenterGuestListQuery } from "../../../guestList/guestListApi"
import type { ServiceCenterGuestListResponse } from "../../../guestList/guestListApi"
import { useExportGuests } from "../../../guestList/hooks/useExportGuests"
import { fetchServiceCenterMeetings, ServiceCenterHomeQuery } from "../../serviceCenterHomeApi"
import type { ServiceCenterMeetingsResponse } from "../../serviceCenterHomeApi"

import { HomePrintSection, printHomeSection } from "../homePrintMode"
import { GuestsInHouseTile } from "./GuestsInHouseTile"
import {
    countGuestPeople,
    toGuestListDisplayRow,
    toGuestListRows,
    toGuestsInHouse,
    toHourSlots,
} from "./guestsOverviewLib"
import { PrintGuestList } from "./PrintGuestList"
import { TodayActivityChart } from "./TodayActivityChart"

const useStyles = makeStyles()(() => ({
    row: HIDDEN_IN_PRINT,
    chartColumn: {
        display: "flex",
        alignItems: "center",
        minWidth: 0,
    },
}))

const selectGuests = (data: ServiceCenterGuestListResponse) => data.guests
const selectBookings = (data: ServiceCenterMeetingsResponse) => data.orders

export function GuestsOverview() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { serviceCenterId } = useCurrentServiceCenter()
    const printPageOnly = usePrintPageOnly()

    const today = dateTimeFactory.now()
    const todayIso = today.toISODate() ?? ""

    const guestsResult = useApiQuery(fetchGuests, {
        queryName: ServiceCenterGuestListQuery.SERVICE_CENTER_GUEST_LIST,
        dependencies: { serviceCenterId, startDate: todayIso, endDate: todayIso },
        select: selectGuests,
        refetchInterval: RefetchIntervals.short,
    })

    const bookingsResult = useApiQuery(fetchServiceCenterMeetings, {
        queryName: ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS,
        dependencies: { serviceCenterId, startDate: todayIso },
        select: selectBookings,
        refetchInterval: RefetchIntervals.short,
    })

    const guests = guestsResult.data
    const bookings = bookingsResult.data

    const guestsInHouse = useMemo(() => toGuestsInHouse(guests ?? []), [guests])
    const slots = useMemo(
        () => toHourSlots(bookings ?? [], guests ?? [], dateTimeFactory),
        [bookings, guests, dateTimeFactory],
    )
    const guestListRows = useMemo(
        () =>
            toGuestListRows(guestsInHouse, bookings ?? [], dateTimeFactory).map((row) =>
                toGuestListDisplayRow(row, l10n, dateTimeFactory),
            ),
        [guestsInHouse, bookings, dateTimeFactory, l10n],
    )

    const formattedToday = dateTimeFactory.formatDate(today)
    const handleExport = useExportGuests({ data: guestsInHouse, startDate: today, endDate: today })
    const handlePrint = () => printHomeSection(HomePrintSection.GUEST_LIST, printPageOnly)

    return (
        <>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing="1.5rem"
                marginBottom="1.5rem"
                className={classes.row}
            >
                <Box flex={1}>
                    <GuestsInHouseTile
                        count={countGuestPeople(guestsInHouse)}
                        isLoading={guestsResult.isInitialLoading}
                        onPrint={handlePrint}
                        onExportCsv={handleExport}
                    />
                </Box>
                <Box flex={1} className={classes.chartColumn}>
                    <TodayActivityChart
                        slots={slots}
                        isLoading={guestsResult.isInitialLoading || bookingsResult.isInitialLoading}
                    />
                </Box>
            </Stack>
            <PrintGuestList rows={guestListRows} date={formattedToday} />
        </>
    )
}
